<template>
  <div
    class="hospital-branding fill-height d-flex flex-column align-center justify-space-between pa-8 position-relative"
    :class="{ 'hospital-branding--dark': isDarkTheme }"
  >
    <div
      class="hospital-branding__grow flex-grow-1 d-flex flex-column align-center justify-center"
    >
      <div class="mb-8 d-flex flex-column align-center text-center">
        <div
          class="hospital-branding__logo-ring mb-6 d-flex align-center justify-center"
          :class="
            resolvedLogoUrl
              ? 'hospital-branding__logo-ring--configured'
              : 'hospital-branding__logo-ring--initial rounded-circle'
          "
        >
          <img
            v-if="resolvedLogoUrl && logoUseNativeImg"
            :src="resolvedLogoUrl"
            alt=""
            class="hospital-branding__logo-img hospital-branding__logo-img--native"
          />
          <v-img
            v-else-if="resolvedLogoUrl"
            :src="resolvedLogoUrl"
            alt=""
            class="hospital-branding__logo-img"
            contain
          />
          <div
            v-else
            class="hospital-branding__initial d-flex align-center justify-center rounded-circle text-h3 font-weight-bold"
          >
            {{ hospitalInitial }}
          </div>
        </div>

        <template v-if="!resolvedLogoUrl">
          <h1 class="text-h4 font-weight-bold mb-2 text-center">
            {{ hospitalConfig.name }}
          </h1>
        </template>
      </div>

      <div
        v-if="helpHtml"
        class="help-panel mt-8 pa-6 align-self-stretch mx-auto w-100"
      >
        <div class="help-html text-body-2" v-html="helpHtml" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  resolveHospitalLogoUrl,
  hospitalLogoUsesNativeImg,
} from "../../utils/helpers/hospitalLogoUrl";

export default {
  name: "HospitalBrandingPanel",
  props: {
    hospitalConfig: {
      type: Object,
      required: true,
    },
    currentView: {
      type: String,
      default: "login",
      validator: (v) =>
        [
          "login",
          "register",
          "forgot-password",
          "restablecer-contrasena",
          "autogestionar-turno",
        ].includes(v),
    },
    hideHelp: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name;
      const value =
        themeName && typeof themeName === "object" ? themeName.value : themeName;
      return value === "dark";
    },
    hospitalInitial() {
      return (this.hospitalConfig.name || "?").charAt(0).toUpperCase();
    },
    resolvedLogoUrl() {
      return resolveHospitalLogoUrl(this.hospitalConfig.logoUrl);
    },
    /** v-img a veces no pinta bien data:/blob:; img nativo es más fiable. */
    logoUseNativeImg() {
      return hospitalLogoUsesNativeImg(this.resolvedLogoUrl);
    },
    helpHtml() {
      if (this.hideHelp) return "";
      if (this.currentView === "register") {
        return this.hospitalConfig.helpTextRegister;
      }
      if (this.currentView === "autogestionar-turno") {
        return this.hospitalConfig.helpTextAutogestion
          || this.hospitalConfig.helpTextLogin;
      }
      return this.hospitalConfig.helpTextLogin;
    },
  },
};
</script>

<style scoped>
.hospital-branding {
  background-color: var(--portal-primary);
  color: rgb(var(--v-theme-on-primary));
  min-height: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
.hospital-branding__grow {
  max-width: 100%;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.55) transparent;
}
.hospital-branding__grow::-webkit-scrollbar {
  width: 8px;
}
.hospital-branding__grow::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.45);
  border-radius: 999px;
}
.hospital-branding__grow::-webkit-scrollbar-track {
  background: transparent;
}
.hospital-branding__logo-ring--initial {
  width: 128px;
  height: 128px;
  background: rgba(var(--v-theme-secondary), 0.24);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.hospital-branding__logo-ring--configured {
  width: auto;
  height: auto;
  max-width: min(620px, 84%);
  max-height: min(320px, 52vh);
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}
.hospital-branding__logo-img {
  width: 100%;
  height: 100%;
  max-width: min(620px, 84%);
  max-height: min(320px, 52vh);
}
.hospital-branding__logo-img :deep(img) {
  object-fit: contain;
}
.hospital-branding__logo-img--native {
  display: block;
  width: auto;
  height: auto;
  max-width: min(620px, 84%);
  max-height: min(320px, 52vh);
  object-fit: contain;
}
.hospital-branding__initial {
  width: 96px;
  height: 96px;
  background: rgb(var(--v-theme-surface));
  color: var(--portal-primary);
}
.help-panel {
  max-width: min(620px, 84%);
  max-height: min(56vh, 520px);
  overflow-y: auto;
  overscroll-behavior: contain;
  text-align: center;
  color: rgb(var(--v-theme-on-primary));
  background: rgba(var(--v-theme-surface), 0.22);
  border: 1px solid rgba(var(--v-theme-surface), 0.12);
  border-radius: 18px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.65) transparent;
}
.help-panel::-webkit-scrollbar {
  width: 8px;
}
.help-panel::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.55);
  border-radius: 999px;
}
.help-panel::-webkit-scrollbar-track {
  background: transparent;
}
.hospital-branding--dark .help-panel {
  color: rgb(var(--v-theme-on-primary));
  background: color-mix(
    in srgb,
    var(--portal-primary) 78%,
    rgb(var(--v-theme-background)) 22%
  );
  border-color: rgba(var(--v-theme-primary), 0.38);
}
.help-html {
  color: inherit;
}
.help-html :deep(p) {
  margin-bottom: 0.5rem;
}
.help-html :deep(p:last-child) {
  margin-bottom: 0;
}
.help-html :deep(ul) {
  list-style: disc outside;
  text-align: left;
  padding-left: 1.25rem;
  margin: 0.75rem auto 0;
  max-width: 34rem;
}
.help-html :deep(li) {
  margin-bottom: 0.25rem;
}
.help-html :deep(strong) {
  color: inherit;
  font-weight: 600;
}
</style>
